/**
 * ServiceRegistry
 *
 * Normalized in-memory registry for service categories, services and a light staff snapshot.
 *
 * Shape:
 *  - categoriesById: Map<number, ServiceCategoryItem>
 *  - categoryOrder: number[]
 *  - servicesById: Map<number, ServiceItem>
 *  - servicesByCategory: Map<number, number[]>
 *  - staffById: Map<number, ServiceStaffRef>
 *
 * This registry is intended to be written to by service-layer fetchers and read by hooks/components.
 */

import { getStaffRegistry } from "@/lib/staff/staffRegistrySingleton";
import { resolveMediaUrl } from "@/lib/resolveMediaUrl";

export type ServiceStaffRef = { id: number; name: string;  specialization?: string; avatar?: string };

export type ServiceItem = {
  id: number;
  yservice_id: number;
  category_id: number;
  title: string;
  price_min: number;
  price_max: number;
  duration_seconds: number;      // service-level default
  comment: string;
  staffIds: number[]; // assigned staff ids
  /** optional per-staff overrides; present only when API provides duration_seconds > 0 */
  staffDurationSeconds?: Record<number, number>;
  /** last-hour fill hint (docs/codebase/last-hour-fill/README.md §3.2) — advisory badge */
  last_hour?: { discount_pct: number; regular_price: number; price: number };
};

export type ServiceCategoryItem = {
  id: number;
  ycategory_id: number;
  name: string;
  count: number;
  serviceIds: number[];
};

export class ServiceRegistry {
  private categoriesById: Map<number, ServiceCategoryItem> = new Map();
  private categoryOrder: number[] = [];
  private servicesById: Map<number, ServiceItem> = new Map();
  private servicesByCategory: Map<number, number[]> = new Map();
  private staffById: Map<number, ServiceStaffRef> = new Map();
  private servicesByStaffId: Map<number, Set<number>> = new Map();
  /** Journal-bookable services per master; see `mergeMasterJournalServices`. */
  private journalServiceIdsByStaff: Map<number, Set<number>> = new Map();

  clear() {
    this.categoriesById.clear();
    this.categoryOrder = [];
    this.servicesById.clear();
    this.servicesByCategory.clear();
    this.staffById.clear();
    this.servicesByStaffId.clear();
    this.journalServiceIdsByStaff.clear();
  }

  /**
   * Merge raw payload from /services/service_categories
   * Expected payload shape (example):
   * {
   *   categories: [{ id, ycategory_id, name, service_count, service_ids: [] }, ...],
   *   services: [{ id, yservice_id, category_id, title, price_min, price_max, duration_seconds, comment, staff: [{id, display_name, avatar_url}, ...] }, ...]
   * }
   */
  mergeFromResponse(payload: any) {
    if (!payload) return;

    // Categories
    const cats = Array.isArray(payload.categories) ? payload.categories : [];
    const parsedCats: ServiceCategoryItem[] = cats.map((c: any) => ({
      id: Number(c.id),
      ycategory_id: Number(c.ycategory_id ?? 0),
      name: String(c.name ?? ""),
      count: Number(c.service_count ?? 0),
      serviceIds: Array.isArray(c.service_ids) ? c.service_ids.map((x: any) => Number(x)) : [],
    }));

    this.categoryOrder = parsedCats.map((c) => c.id);
    parsedCats.forEach((c) => {
      this.categoriesById.set(c.id, c);
      this.servicesByCategory.set(c.id, [...c.serviceIds]);
    });

    // Services
    const services = Array.isArray(payload.services) ? payload.services : [];
    for (const s of services) {

      const id = Number(s.id);
      // build staff id list
      const staffIds: number[] = Array.isArray(s.staff) ? s.staff.map((st: any) => Number(st.id)) : [];

      // Build per-staff duration lookup (only keep > 0 to allow "0" to mean "use service default")
      const staffDurationSeconds: Record<number, number> = {};
      // keep light staff snapshot and optionally enrich StaffRegistry
      if (Array.isArray(s.staff)) {
        for (const st of s.staff) {
          const stId = Number(st.id);
          const stDur = Number(st.duration_seconds ?? 0);
          if (stId && stDur > 0) staffDurationSeconds[stId] = stDur;

          const ref: ServiceStaffRef = {
            id: stId,
            name: String(st.display_name ?? st.name ?? ""),
            avatar: resolveMediaUrl(st.avatar_url ?? st.image) || "",
            specialization: String(st.specialization ?? ""),
          };
          this.staffById.set(ref.id, ref);

          // Best-effort enrichment of StaffRegistry (don't fail if API differs)
          try {
            getStaffRegistry().addOrUpdateStaff({
              id: ref.id,
              name: ref.name,
              specialization: ref.specialization || "none",
              avatar: ref.avatar || "/placeholder-user.jpg",
            } as any);
          } catch {
            // ignore if staffRegistry API differs
          }
        }
      }

      const item: ServiceItem = {
        id,
        yservice_id: Number(s.yservice_id ?? 0),
        category_id: Number(s.category_id ?? 0),
        title: String(s.title ?? ""),
        price_min: Number(s.price_min ?? 0),
        price_max: Number(s.price_max ?? 0),
        duration_seconds: Number(s.duration_seconds ?? 0),
        comment: String(s.comment ?? ""),
        staffIds,
        // NEW: per-staff overrides (only includes entries with >0 durations)
        staffDurationSeconds,
        last_hour:
          s.last_hour && Number(s.last_hour.discount_pct) > 0
            ? {
                discount_pct: Number(s.last_hour.discount_pct),
                regular_price: Number(s.last_hour.regular_price ?? 0),
                price: Number(s.last_hour.price ?? 0),
              }
            : undefined,
      };

      this.servicesById.set(id, item);

      // build reverse index: staffId -> Set(serviceId)
      for (const stId of staffIds) {
        if (!this.servicesByStaffId.has(stId)) this.servicesByStaffId.set(stId, new Set());
        this.servicesByStaffId.get(stId)!.add(id);
      }
    }

    // If backend returned staff-centric mapping, hydrate reverse index
    if (Array.isArray(payload.staff)) {
      for (const st of payload.staff) {
        const stId = Number(st.id);
        // keep staff snapshot (if not already present)
        const ref: ServiceStaffRef = {
          id: stId,
          name: String(st.display_name ?? st.name ?? ""),
          avatar: resolveMediaUrl(st.avatar_url ?? st.image) || "",
        };
        this.staffById.set(ref.id, ref);
        try {
          getStaffRegistry().addOrUpdateStaff({
            id: ref.id,
            name: ref.name,
            specialization: "",
            avatar: ref.avatar || "/placeholder-user.jpg",
          } as any);
        } catch { /* ignore */ }

        // payload shape may vary: categories[].service_ids OR direct service_ids array
        if (Array.isArray(st.categories)) {
          for (const cat of st.categories) {
            if (Array.isArray(cat.service_ids)) {
              for (const sid of cat.service_ids) {
                const sidNum = Number(sid);
                if (!this.servicesByStaffId.has(stId)) this.servicesByStaffId.set(stId, new Set());
                this.servicesByStaffId.get(stId)!.add(sidNum);
              }
            }
          }
        }
        if (Array.isArray(st.service_ids)) {
          for (const sid of st.service_ids) {
            const sidNum = Number(sid);
            if (!this.servicesByStaffId.has(stId)) this.servicesByStaffId.set(stId, new Set());
            this.servicesByStaffId.get(stId)!.add(sidNum);
          }
        }
      }
    }
  }

  // ---------- getters ----------

  getCategories(): ServiceCategoryItem[] {
    return this.categoryOrder.map((id) => this.categoriesById.get(id)!).filter(Boolean);
  }

  getServicesForCategory(categoryId: number): ServiceItem[] {
    const ids = this.servicesByCategory.get(categoryId) ?? [];
    return ids.map((sid) => this.servicesById.get(sid)!).filter(Boolean);
  }

  getServiceById(id: number): ServiceItem | undefined {
    return this.servicesById.get(id);
  }

  getStaffRef(id: number): ServiceStaffRef | undefined {
    return this.staffById.get(id);
  }

  getServicesByIds(ids: number[]): ServiceItem[] {
    return ids.map((id) => this.servicesById.get(id)).filter(Boolean) as ServiceItem[];
  }

  getServiceStaffIds(serviceId: number): number[] {
    const s = this.servicesById.get(serviceId);
    return s ? s.staffIds : [];
  }

  getStaffRefsForService(serviceId: number): ServiceStaffRef[] {
    const ids = this.getServiceStaffIds(serviceId);
    return ids.map((id) => this.staffById.get(id)).filter(Boolean) as ServiceStaffRef[];
  }

  /** IDs of services provided by a given master */
  getServiceIdsByMaster(masterId: number): number[] {
    return Array.from(this.servicesByStaffId.get(masterId) ?? []);
  }

  /**
   * Records what a master may be booked for **in the journal**, from
   * `/company/master/{id}/services`.
   *
   * Deliberately kept apart from `servicesByStaffId`, which comes from the service
   * catalogue and lists every assignment regardless of the per-staff "Журнал записей"
   * toggle. The journal picker honours that toggle, so anything deciding what an admin
   * may assign — the appointment modal, drag & drop — has to read this narrower set,
   * or it would permit a move the modal itself would then refuse.
   */
  mergeMasterJournalServices(masterId: number, serviceIds: number[]): void {
    const ids = serviceIds.map(Number).filter((id) => Number.isFinite(id));
    this.journalServiceIdsByStaff.set(Number(masterId), new Set(ids));
  }

  /**
   * Service ids a master may be booked for in the journal.
   * `undefined` means "never loaded" — callers must not read that as "offers nothing".
   */
  getJournalServiceIds(masterId: number): number[] | undefined {
    const set = this.journalServiceIdsByStaff.get(Number(masterId));
    return set ? Array.from(set) : undefined;
  }

  /** Full service objects provided by a given master */
  getServicesByMaster(masterId: number): ServiceItem[] {
    return this.getServiceIdsByMaster(masterId)
      .map((sid) => this.servicesById.get(sid))
      .filter(Boolean) as ServiceItem[];
  }

  /** Masters who can perform ALL of the provided service IDs (intersection) */
  getMasterIdsOfferingAll(serviceIds: number[]): number[] {
    if (!serviceIds.length) return Array.from(this.servicesByStaffId.keys());
    // Start from staff set for the first service, then intersect
    const [first, ...rest] = serviceIds;
    let acc = new Set(this.getServiceStaffIds(first));
    for (const sid of rest) {
      const next = new Set(this.getServiceStaffIds(sid));
      acc = new Set([...acc].filter((id) => next.has(id)));
    }
    return Array.from(acc).map(Number);
  }

  /** Convenience: service IDs a master provides inside a specific category */
  getServiceIdsByMasterInCategory(masterId: number, categoryId: number): number[] {
    const byCat = new Set(this.getServicesForCategory(categoryId).map((s) => s.id));
    return this.getServiceIdsByMaster(masterId).filter((sid) => byCat.has(sid));
  }

  /**
   * Return a JSON-friendly snapshot for use in React Query `select`
   */
  getSnapshot() {
    const categories: Record<number, ServiceCategoryItem> = {};
    this.categoriesById.forEach((v, k) => {
      categories[k] = { ...v };
    });

    const services: Record<number, ServiceItem> = {};
    this.servicesById.forEach((v, k) => {
      services[k] = { ...v };
    });

    return {
      categoryOrder: [...this.categoryOrder],
      categories,
      services,
    };
  }
}
